// ANX-087 — API-клиент staff-админки. Отдельный контур: свой токен
// (audience=staff) из useStaffAuthStore, refresh через /staff/auth/refresh.
import { createApiClient, type ApiClient } from './client';
import { useStaffAuthStore, type StaffSession } from '../store/staffAuth';

let _client: ApiClient | null = null;

function origin(): string {
  return (import.meta.env.VITE_API_ORIGIN as string | undefined) ?? '';
}

// Низкоуровневый refresh без авто-retry (иначе рекурсия на 401 у /refresh).
async function rawStaffRefresh(refresh: string): Promise<StaffSession | null> {
  try {
    const res = await fetch(`${origin()}/api/v1/staff/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh }),
    });
    if (!res.ok) return null;
    const j = (await res.json()) as RawAuth;
    return toSession(j);
  } catch {
    return null;
  }
}

export function getStaffClient(): ApiClient {
  if (_client) return _client;
  _client = createApiClient({
    origin: origin(),
    getAccess: () => useStaffAuthStore.getState().session?.access ?? null,
    refresh: async () => {
      const s = useStaffAuthStore.getState().session;
      if (!s) return null;
      const next = await rawStaffRefresh(s.refresh);
      if (!next) {
        useStaffAuthStore.getState().clear();
        return null;
      }
      useStaffAuthStore.getState().setSession(next);
      return next.access;
    },
    onAuthFail: () => useStaffAuthStore.getState().clear(),
  });
  return _client;
}

interface RawAuth {
  access: string;
  refresh: string;
  staff: { id: string; email: string; name: string };
}

function toSession(r: RawAuth): StaffSession {
  return { access: r.access, refresh: r.refresh, staff: r.staff };
}

// Результат пароля: либо готовая сессия, либо требование MFA-шага (087a').
export type StaffLoginResult =
  | { kind: 'session'; session: StaffSession }
  | { kind: 'mfa'; mfaSession: string }
  | { kind: 'enroll'; enrollSession: string };

export async function staffLogin(email: string, password: string): Promise<StaffLoginResult> {
  const res = await fetch(`${origin()}/api/v1/staff/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const code = res.status === 401 ? 'auth_failed' : res.status === 403 ? 'forbidden' : 'error';
    throw new Error(code);
  }
  const j = (await res.json()) as Partial<RawAuth> & {
    requires_mfa?: boolean;
    mfa_session?: string;
    requires_enrollment?: boolean;
    enrollment_session?: string;
  };
  if (j.requires_mfa && j.mfa_session) return { kind: 'mfa', mfaSession: j.mfa_session };
  if (j.requires_enrollment && j.enrollment_session)
    return { kind: 'enroll', enrollSession: j.enrollment_session };
  return { kind: 'session', session: toSession(j as RawAuth) };
}

// --- passkey-MFA (087a', публичные эндпойнты) ------------------------------

async function staffPasskeyPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${origin()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(res.status === 401 ? 'session_invalid' : 'passkey_failed');
  return (await res.json()) as T;
}

interface BeginCeremony {
  options: { publicKey: unknown };
  session_token: string;
}

export function staffPasskeyBeginEnroll(enrollSession: string): Promise<BeginCeremony> {
  return staffPasskeyPost('/api/v1/staff/auth/passkey/begin-register', {
    enrollment_session: enrollSession,
  });
}

export async function staffPasskeyFinishEnroll(sessionToken: string, attestation: unknown): Promise<StaffSession> {
  const r = await staffPasskeyPost<RawAuth>('/api/v1/staff/auth/passkey/finish-register', {
    session_token: sessionToken,
    attestation,
  });
  return toSession(r);
}

export function staffPasskeyBeginLogin(mfaSession: string): Promise<BeginCeremony> {
  return staffPasskeyPost('/api/v1/staff/auth/passkey/begin-login', { mfa_session: mfaSession });
}

export async function staffPasskeyFinishLogin(sessionToken: string, assertion: unknown): Promise<StaffSession> {
  const r = await staffPasskeyPost<RawAuth>('/api/v1/staff/auth/passkey/finish-login', {
    session_token: sessionToken,
    assertion,
  });
  return toSession(r);
}

export async function staffLogout(): Promise<void> {
  try {
    await getStaffClient().request('/api/v1/staff/auth/logout', { method: 'POST' });
  } catch {
    // всё равно чистим локально
  }
  useStaffAuthStore.getState().clear();
}

// --- Клиенты (tenants) -----------------------------------------------------

export interface StaffTenant {
  id: string;
  slug: string;
  name: string;
  plan: string;
  status: string;
  created_at: string;
  seats_used: number;
  dialogs_month: number;
  sub_status: string | null;
  billing_mode: string | null;
  current_period_end: string | null;
}

export interface StaffTenantOperator {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  last_seen_at: string | null;
  created_at: string;
}

export interface StaffTenantInvoice {
  id: string;
  number: string;
  plan: string;
  amount_minor: number;
  currency: string;
  status: string;
  issued_at: string;
  paid_at: string | null;
}

export async function listStaffTenants(search = ''): Promise<StaffTenant[]> {
  const q = search ? `?search=${encodeURIComponent(search)}` : '';
  const r = await getStaffClient().request<{ tenants: StaffTenant[] }>(`/api/v1/staff/tenants${q}`);
  return r.tenants ?? [];
}

export async function getStaffTenant(id: string): Promise<{
  tenant: StaffTenant;
  operators: StaffTenantOperator[];
  invoices: StaffTenantInvoice[];
}> {
  return getStaffClient().request(`/api/v1/staff/tenants/${id}`);
}

export interface StaffMetrics {
  totals: {
    tenants_total: number;
    tenants_active: number;
    tenants_suspended: number;
    operators_total: number;
    conversations_30d: number;
    new_tenants_30d: number;
  };
  plans: { plan: string; tenants: number; active_subs: number; price_rub_minor: number }[];
  mrr_rub_minor: number;
}

export async function getStaffMetrics(): Promise<StaffMetrics> {
  return getStaffClient().request('/api/v1/staff/metrics');
}

export async function staffSetPlan(id: string, plan: string): Promise<void> {
  await getStaffClient().request(`/api/v1/staff/tenants/${id}/plan`, {
    method: 'POST',
    json: { plan },
  });
}

export async function staffSetStatus(id: string, status: string): Promise<void> {
  await getStaffClient().request(`/api/v1/staff/tenants/${id}/status`, {
    method: 'POST',
    json: { status },
  });
}

export async function staffGrant(id: string, plan: string, days: number): Promise<void> {
  await getStaffClient().request(`/api/v1/staff/tenants/${id}/grant`, {
    method: 'POST',
    json: { plan, days },
  });
}

export interface ImpersonateResult {
  access: string;
  expires_in: number;
  operator: { id: string; tenant_id: string };
}

export async function staffImpersonate(id: string): Promise<ImpersonateResult> {
  return getStaffClient().request(`/api/v1/staff/tenants/${id}/impersonate`, { method: 'POST' });
}
